import { useState } from "react";
import { Calendar, History } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CircleEventCard } from "./CircleEventCard";
import { CircleEventForm } from "./CircleEventForm";
import { useCircleEvents } from "@/hooks/useCircleEvents";
import { useCircleHostEvents } from "@/hooks/useCircleHostEvents";
import { useCircleBooking } from "@/hooks/useCircleBooking";
import { useMyCircleBookings, MyCircleBooking } from "@/hooks/useMyCircleBookings";
import { useAuth } from "@/components/auth/AuthProvider";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { CircleEvent } from "@/types/circle";
import { CircleCancelDialog } from "./CircleCancelDialog";

interface CircleEventsListProps {
  isCircleMember?: boolean;
}

export function CircleEventsList({ isCircleMember = false }: CircleEventsListProps) {
  const { events, isLoading } = useCircleEvents();
  const { cancelEvent } = useCircleHostEvents();
  const { cancelBooking } = useCircleBooking();
  const { upcomingBookings } = useMyCircleBookings();
  const { userId } = useAuth();
  const [editingEvent, setEditingEvent] = useState<CircleEvent | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<CircleEvent | null>(null);
  const [cancellingBooking, setCancellingBooking] = useState<MyCircleBooking | null>(null);

  const handleDeleteConfirm = async () => {
    if (!deletingEvent) return;
    await cancelEvent.mutateAsync({ 
      eventId: deletingEvent.id, 
      reason: "Withdrawn by host" 
    });
    setDeletingEvent(null);
  };

  const handleCancelBookingConfirm = () => {
    if (cancellingBooking) {
      cancelBooking.mutate({ bookingId: cancellingBooking.id });
      setCancellingBooking(null);
    }
  };

  const getBookingForEvent = (eventId: string) => {
    return upcomingBookings.find(b => b.circle_event_id === eventId);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mb-4">
            <Calendar className="w-6 h-6 text-violet-500" />
          </div>
          <h3 className="font-medium text-foreground mb-1">No Events Yet</h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            There are no upcoming Circle events at the moment. Check back soon or lead your own!
          </p>
        </CardContent>
      </Card>
    );
  }

  const now = new Date();
  
  // Separate events by status and timing
  const pendingEvents = events.filter(e => e.status === "pending" && e.host_id === userId);
  const rejectedEvents = events.filter(e => e.status === "rejected" && e.host_id === userId);
  const upcomingEvents = events.filter(e => e.status === "approved" && new Date(e.end_time) > now);
  const pastEvents = events.filter(e => e.status === "approved" && new Date(e.end_time) <= now);

  return (
    <>
      <div className="space-y-6">
        {/* User's pending events */}
        {pendingEvents.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Under Review</h2>
              <p className="text-sm text-muted-foreground">
                Your events awaiting admin approval
              </p>
            </div>
            <div className={`grid gap-4 ${pendingEvents.length === 1 ? "sm:w-1/2 mx-auto" : "sm:grid-cols-2"}`}>
              {pendingEvents.map((event) => (
                <CircleEventCard 
                  key={event.id} 
                  event={event}
                  isOwnEvent={true}
                  onEdit={() => setEditingEvent(event)}
                  onDelete={() => setDeletingEvent(event)}
                />
              ))}
            </div>
          </div>
        )}

        {/* User's rejected events */}
        {rejectedEvents.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Rejected</h2>
              <p className="text-sm text-muted-foreground">
                Events not approved by admin
              </p>
            </div>
            <div className={`grid gap-4 ${rejectedEvents.length === 1 ? "sm:w-1/2 mx-auto" : "sm:grid-cols-2"}`}>
              {rejectedEvents.map((event) => (
                <CircleEventCard 
                  key={event.id} 
                  event={event}
                  isOwnEvent={true}
                  onEdit={() => setEditingEvent(event)}
                  onDelete={() => setDeletingEvent(event)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming events */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Upcoming Events</h2>
              <p className="text-sm text-muted-foreground">
                {upcomingEvents.length} event{upcomingEvents.length !== 1 ? "s" : ""} available
              </p>
            </div>
          </div>
          {upcomingEvents.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No upcoming events yet. Check back soon!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className={`grid gap-4 ${upcomingEvents.length === 1 ? "sm:w-1/2 mx-auto" : "sm:grid-cols-2"}`}>
              {upcomingEvents.map((event) => {
                const isOwnEvent = event.host_id === userId;
                const booking = getBookingForEvent(event.id);
                return (
                  <CircleEventCard 
                    key={event.id} 
                    event={event}
                    isOwnEvent={isOwnEvent}
                    isBooked={!!booking}
                    isCircleMember={isCircleMember}
                    onEdit={isOwnEvent ? () => setEditingEvent(event) : undefined}
                    onCancelBooking={booking ? () => setCancellingBooking(booking) : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Past events */}
        {pastEvents.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <History className="w-5 h-5 text-muted-foreground" />
                Past Events
              </h2>
              <p className="text-sm text-muted-foreground">
                Events you hosted or attended
              </p>
            </div>
            <div className={`grid gap-4 ${pastEvents.length === 1 ? "sm:w-1/2 mx-auto" : "sm:grid-cols-2"}`}>
              {pastEvents.map((event) => {
                const isOwnEvent = event.host_id === userId;
                return (
                  <CircleEventCard 
                    key={event.id} 
                    event={event}
                    isOwnEvent={isOwnEvent}
                    isCircleMember={isCircleMember}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Edit Sheet */}
      <Sheet open={!!editingEvent} onOpenChange={(open) => !open && setEditingEvent(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Event</SheetTitle>
            <SheetDescription>Update your Circle event details</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {editingEvent && (
              <CircleEventForm event={editingEvent} onSuccess={() => setEditingEvent(null)} />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingEvent} onOpenChange={(open) => !open && setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw Event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deletingEvent?.title}" from review. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Booking Dialog */}
      <CircleCancelDialog
        booking={cancellingBooking}
        onClose={() => setCancellingBooking(null)}
        onConfirm={handleCancelBookingConfirm}
        isPending={cancelBooking.isPending}
      />
    </>
  );
}
